import { EditOutlined } from '@mui/icons-material'
import { Box, Divider, Grid, IconButton, Menu, MenuItem, Tooltip } from '@mui/material'
import { GridTripleDotsVerticalIcon } from '@mui/x-data-grid'
import type { MouseEvent, ReactNode } from 'react'
import { useState } from 'react'

import CustomIconButton from '@/components/inputs/CustomIconButton'

export enum NodeMenuItemType {
  action,
  divider,
}

export type NodeMenuItem = NodeMenuAction | NodeMenuDivider

export interface NodeMenuDivider {
  type: NodeMenuItemType.divider
}

export interface NodeMenuAction {
  /** When set, the action is shown but cannot be performed, and this explains why. */
  disabledReason?: string
  icon: ReactNode
  label: string
  perform: () => Promise<void>
  type: NodeMenuItemType.action
}

export interface NodeActionsProps {
  editAction?: () => void
  editTitle?: string
  menuActions?: NodeMenuItem[]
}

export function NodeActions({ editAction, editTitle, menuActions }: Readonly<NodeActionsProps>) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const openMenu = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)
  const closeMenu = () => setAnchorEl(null)

  function renderMenuAction(menuAction: NodeMenuAction) {
    const { disabledReason } = menuAction
    const menuItem = (
      <MenuItem
        key={menuAction.label}
        // aria-disabled rather than disabled: a truly disabled MenuItem receives no pointer events
        // and cannot be focused, so its reason would reach neither the mouse nor the keyboard. This
        // keeps the item reachable and announced as disabled, and the click below is what refuses.
        aria-disabled={disabledReason === undefined ? undefined : true}
        aria-label={menuAction.label}
        sx={
          disabledReason === undefined
            ? undefined
            : {
                cursor: 'default',
                opacity: (theme) => theme.palette.action.disabledOpacity,
                '&:hover': { backgroundColor: 'transparent' },
              }
        }
        onClick={() => {
          if (disabledReason !== undefined) {
            return
          }
          closeMenu()
          menuAction.perform()
        }}
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 'auto' }} sx={{ display: 'flex' }}>
            {menuAction.icon}
          </Grid>
          <Grid size={{ xs: 'auto' }}>{menuAction.label}</Grid>
        </Grid>
      </MenuItem>
    )
    if (disabledReason === undefined) {
      return menuItem
    }
    return (
      // describeChild, so the reason is read out as the item's description on focus rather than
      // taking the place of its label. Tooltip renders no wrapper element, so MenuList still sees
      // the item as its own child and arrow-key navigation keeps working.
      <Tooltip key={menuAction.label} describeChild title={disabledReason}>
        {menuItem}
      </Tooltip>
    )
  }

  return (
    <Box>
      {editAction && (
        <CustomIconButton
          Icon={EditOutlined}
          iconProps={{ titleAccess: editTitle }}
          tooltipProps={{ title: editTitle }}
          onClick={editAction}
        />
      )}
      {menuActions && (
        <>
          <IconButton
            aria-controls="simple-menu"
            aria-haspopup="true"
            aria-label="More actions"
            data-testid="menu-button"
            title="More actions"
            onClick={openMenu}
          >
            <GridTripleDotsVerticalIcon />
          </IconButton>
          <Menu
            // Or arrow keys would skip the aria-disabled items, hiding the reason from the keyboard
            MenuListProps={{ disabledItemsFocusable: true }}
            anchorEl={anchorEl}
            anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
            open={Boolean(anchorEl)}
            onClose={closeMenu}
          >
            {menuActions.map((nodeMenuItem, index) => {
              switch (nodeMenuItem.type) {
                case NodeMenuItemType.action:
                  return renderMenuAction(nodeMenuItem)
                case NodeMenuItemType.divider:
                  // eslint-disable-next-line react/no-array-index-key -- Dividers have no unique identifier
                  return <Divider key={'divider-' + index} />
              }
            })}
          </Menu>
        </>
      )}
    </Box>
  )
}
